/**
 * MoveConfirmDialog — a small ACCESSIBLE confirmation surface (role=alertdialog)
 * for out-of-flow Board moves: a backward / regression drag on the Lenses
 * board persists nothing until the user confirms here. NO browser
 * `window.confirm` — this is an in-app modal with a scrim, a focus trap,
 * Esc-to-cancel and focus-restore, modelled directly on LeadDetailDrawer's
 * focus handling. It owns no business state: Confirm/Cancel are the caller's.
 */
import React, { useEffect, useRef } from 'react';

/** Tab-trap focusable selector (visible, enabled, tabbable) — mirrors the drawer. */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function MoveConfirmDialog({
  title,
  body,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  restoreFocusId,
}: {
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** data-lead-id of the card to return focus to on close (a11y). */
  restoreFocusId?: string | null;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  // Read in the unmount cleanup to re-find the originating card (it has been
  // re-rendered into a new DOM node after a confirmed move).
  const restoreIdRef = useRef(restoreFocusId);
  restoreIdRef.current = restoreFocusId;

  // Esc cancels.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  // Focus management (a11y): on open, move focus to the primary action and lock
  // background scroll; on close, restore focus to the card that opened the
  // dialog (re-found by its stable data-lead-id), exactly like LeadDetailDrawer.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    confirmRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      const id = restoreIdRef.current;
      const origin =
        (id && document.querySelector<HTMLElement>(`[data-lead-id="${id}"]`)) ||
        (opener && opener.isConnected ? opener : null);
      origin?.focus?.();
    };
  }, []);

  // Trap Tab within the panel so the keyboard can't reach the locked backdrop.
  function trapTab(e: React.KeyboardEvent) {
    if (e.key !== 'Tab') return;
    const node = panelRef.current;
    if (!node) return;
    const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
    if (items.length === 0) {
      e.preventDefault();
      node.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === node) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="move-confirm-title"
      aria-describedby="move-confirm-body"
      onKeyDown={trapTab}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70, // above the drawer (60)
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      {/* scrim — click cancels */}
      <div
        onClick={onCancel}
        style={{ position: 'absolute', inset: 0, background: 'rgba(20,20,30,.32)' }}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        style={{
          position: 'relative',
          width: 'min(420px, 92vw)',
          background: 'var(--paper, #fff)',
          border: '1px solid var(--rule, #E5E7EB)',
          borderRadius: 'var(--r-md, 13px)',
          boxShadow: 'var(--e-2, 0 18px 48px -12px rgba(20,20,30,.34))',
          padding: 18,
          outline: 'none',
        }}
      >
        <h2
          id="move-confirm-title"
          style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--ink, #1A1A1A)' }}
        >
          {title}
        </h2>
        <p
          id="move-confirm-body"
          style={{
            margin: '8px 0 16px',
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--ink-soft, #4B5563)',
          }}
        >
          {body}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '7px 14px',
              borderRadius: 'var(--r-pill, 999px)',
              border: '1px solid var(--rule-strong, #D1D5DB)',
              background: 'var(--paper, #fff)',
              color: 'var(--ink-soft, #4B5563)',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            style={{
              padding: '7px 14px',
              borderRadius: 'var(--r-pill, 999px)',
              border: '1px solid var(--accent, #008069)',
              background: 'var(--accent, #008069)',
              color: '#fff',
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
